import React, { useState } from 'react'
import { api } from '../api/client'
import AuthCard, { authCardStyles as s } from './AuthCard'
import Button from './Button'
import Input from './Input'
import ConsentCheckbox from './ConsentCheckbox'
import {
  FORGOT_LETTER_SENT,
  MIN_PASSWORD_LENGTH,
  OTHER_SESSIONS_NOTICE,
  PASSWORD_RESET_DONE,
  PASSWORD_TOO_SHORT,
  PASSWORDS_DO_NOT_MATCH,
  translateAuthError
} from '../authMessages'

// Восстановление доступа двумя путями. Основной — письмо со ссылкой. Второй —
// подтверждение по сохранённому API-токену Финтабло: письмо уходит на личный
// ящик того, кто регистрировал аккаунт, и бесполезно, когда доступ передаётся
// между сотрудниками. Токен доказывает владение компании, а не человека.
export default function LoginPage ({ onLogin }) {
  const [mode, setMode] = useState('login') // 'login' | 'register' | 'recover'
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fintabloToken, setFintabloToken] = useState('')
  // Согласие на обработку персональных данных — только при регистрации.
  // Снято по умолчанию; без него кнопка неактивна, а сервер отклонит запрос.
  const [consentAccepted, setConsentAccepted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // 'forgot' → 'letter_sent' | 'challenge' → 'password' → 'done'
  const [recoverStep, setRecoverStep] = useState('forgot')
  const [recoverEmail, setRecoverEmail] = useState('')
  const [recoverToken, setRecoverToken] = useState('')
  const [resetToken, setResetToken] = useState(null)
  const [newPassword, setNewPassword] = useState('')
  const [repeatPassword, setRepeatPassword] = useState('')
  const [recoveredUser, setRecoveredUser] = useState(null)

  async function handleSubmit (e) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const result = mode === 'login'
        ? await api.login(email, password)
        : await api.register(email, password, fintabloToken, consentAccepted)
      localStorage.setItem('ch_token', result.token)
      onLogin(result.user)
    } catch (err) {
      setError(translateAuthError(err))
    } finally {
      setLoading(false)
    }
  }

  function switchMode (next) {
    setMode(next)
    setError(null)
  }

  function startRecovery () {
    setMode('recover')
    setRecoverStep('forgot')
    setRecoverEmail(email)
    setRecoverToken('')
    setResetToken(null)
    setNewPassword('')
    setRepeatPassword('')
    setError(null)
  }

  async function handleForgot (e) {
    e.preventDefault()
    if (loading) return
    setError(null)
    setLoading(true)
    try {
      await api.forgotPassword(recoverEmail.trim())
      setRecoverStep('letter_sent')
    } catch (err) {
      setError(translateAuthError(err))
    } finally {
      setLoading(false)
    }
  }

  async function handleChallenge (e) {
    e.preventDefault()
    if (loading) return
    setError(null)
    setLoading(true)
    try {
      const { reset_token: token } = await api.verifyPasswordReset(
        recoverEmail.trim(),
        recoverToken.trim()
      )
      setResetToken(token)
      setRecoverStep('password')
    } catch (err) {
      setError(translateAuthError(err))
    } finally {
      setLoading(false)
    }
  }

  async function handleNewPassword (e) {
    e.preventDefault()
    if (loading) return

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(PASSWORD_TOO_SHORT)
      return
    }
    if (newPassword !== repeatPassword) {
      setError(PASSWORDS_DO_NOT_MATCH)
      return
    }

    setError(null)
    setLoading(true)
    try {
      const result = await api.confirmPasswordReset(resetToken, newPassword)
      localStorage.setItem('ch_token', result.token)
      setRecoveredUser(result.user)
      setRecoverStep('done')
    } catch (err) {
      // Истёкшее подтверждение — не ошибка ввода: возвращаем на первый шаг, но
      // адрес не теряем, иначе пауза в десять минут стоит повторного набора.
      const expired = err?.code === 'RESET_TOKEN_EXPIRED'
      if (expired) {
        setResetToken(null)
        setNewPassword('')
        setRepeatPassword('')
        setRecoverStep('challenge')
      }
      setError(translateAuthError(err))
    } finally {
      setLoading(false)
    }
  }

  const backToLogin = (
    <button style={s.link} type='button' onClick={() => switchMode('login')}>
      Вернуться к входу
    </button>
  )

  if (mode === 'recover') {
    return (
      <AuthCard>
        {recoverStep === 'forgot' && (
          <form onSubmit={handleForgot} style={s.form}>
            <div style={s.stepTitle}>Восстановление доступа</div>

            <div style={s.field}>
              <label style={s.label} htmlFor='forgot-email'>Email аккаунта</label>
              <Input
                aria-label='Email аккаунта'
                autoComplete='email'
                autoFocus
                id='forgot-email'
                onChange={e => setRecoverEmail(e.target.value)}
                placeholder='you@example.com'
                required
                type='email'
                value={recoverEmail}
              />
              <div style={s.hint}>
                Пришлём письмо со ссылкой для смены пароля.
              </div>
            </div>

            {error && <div style={s.error} role='alert'>{error}</div>}

            <Button
              type='submit'
              variant='solid'
              loading={loading}
              style={{ width: '100%', marginTop: 4 }}
            >
              Отправить ссылку
            </Button>
            <button style={s.link} type='button' onClick={() => { setError(null); setRecoverStep('challenge') }}>
              Нет доступа к почте аккаунта?
            </button>
            {backToLogin}
          </form>
        )}

        {recoverStep === 'letter_sent' && (
          <div style={s.form}>
            <div style={s.notice} role='status'>{FORGOT_LETTER_SENT}</div>
            <button style={s.link} type='button' onClick={() => { setError(null); setRecoverStep('challenge') }}>
              Нет доступа к почте аккаунта?
            </button>
            {backToLogin}
          </div>
        )}

        {recoverStep === 'challenge' && (
          <form onSubmit={handleChallenge} style={s.form}>
            <div style={s.stepTitle}>Подтвердите доступ к аккаунту</div>

            <div style={s.field}>
              <label style={s.label} htmlFor='recover-email'>Email аккаунта</label>
              <Input
                aria-label='Email аккаунта'
                autoComplete='email'
                id='recover-email'
                onChange={e => setRecoverEmail(e.target.value)}
                placeholder='you@example.com'
                required
                type='email'
                value={recoverEmail}
              />
            </div>

            <div style={s.field}>
              <label style={s.label} htmlFor='recover-token'>API-токен Финтабло</label>
              <Input
                aria-label='API-токен Финтабло'
                autoComplete='off'
                id='recover-token'
                onChange={e => setRecoverToken(e.target.value)}
                placeholder='Токен из настроек Финтабло'
                required
                type='password'
                value={recoverToken}
              />
              <div style={s.hint}>
                Введите API-токен Финтабло, сохранённый в платформе. Если после
                подключения вы перевыпустили токен в Финтабло, создайте новый
                аккаунт или обратитесь в чат техподдержки.
              </div>
            </div>

            {error && <div style={s.error} role='alert'>{error}</div>}

            <Button
              type='submit'
              variant='solid'
              loading={loading}
              style={{ width: '100%', marginTop: 4 }}
            >
              Продолжить
            </Button>
            {backToLogin}
          </form>
        )}

        {recoverStep === 'password' && (
          <form onSubmit={handleNewPassword} style={s.form}>
            <div style={s.stepTitle}>Доступ подтверждён. Задайте новый пароль.</div>

            <div style={s.field}>
              <label style={s.label} htmlFor='recover-new-password'>Новый пароль</label>
              <Input
                aria-label='Новый пароль'
                autoComplete='new-password'
                id='recover-new-password'
                onChange={e => setNewPassword(e.target.value)}
                placeholder='Минимум 8 символов'
                required
                type='password'
                value={newPassword}
              />
            </div>

            <div style={s.field}>
              <label style={s.label} htmlFor='recover-repeat-password'>Повторите пароль</label>
              <Input
                aria-label='Повторите пароль'
                autoComplete='new-password'
                id='recover-repeat-password'
                onChange={e => setRepeatPassword(e.target.value)}
                placeholder='Ещё раз новый пароль'
                required
                type='password'
                value={repeatPassword}
              />
            </div>

            {error && <div style={s.error} role='alert'>{error}</div>}

            <Button
              type='submit'
              variant='solid'
              loading={loading}
              style={{ width: '100%', marginTop: 4 }}
            >
              Сохранить пароль
            </Button>
            {backToLogin}
          </form>
        )}

        {recoverStep === 'done' && (
          <div style={s.form}>
            <div style={s.stepTitle}>{PASSWORD_RESET_DONE}</div>
            <div style={s.hint}>{OTHER_SESSIONS_NOTICE}</div>
            <Button
              variant='solid'
              onClick={() => onLogin(recoveredUser)}
              style={{ width: '100%', marginTop: 4 }}
            >
              Продолжить
            </Button>
          </div>
        )}
      </AuthCard>
    )
  }

  return (
    <AuthCard>
      <div style={s.tabs}>
        <button
          style={{ ...s.tab, ...(mode === 'login' ? s.tabActive : {}) }}
          onClick={() => switchMode('login')}
        >
          Войти
        </button>
        <button
          style={{ ...s.tab, ...(mode === 'register' ? s.tabActive : {}) }}
          onClick={() => switchMode('register')}
        >
          Регистрация
        </button>
      </div>

      <form onSubmit={handleSubmit} style={s.form}>
        <div style={s.field}>
          <label style={s.label}>Email</label>
          <Input
            type='email'
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder='you@example.com'
            required
            autoComplete='email'
          />
        </div>

        <div style={s.field}>
          <label style={s.label}>Пароль</label>
          <Input
            type='password'
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder={mode === 'register' ? 'Минимум 8 символов' : ''}
            required
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
          />
        </div>

        {mode === 'register' && (
          <div style={s.field}>
            <label style={s.label}>
              API-токен Финтабло
            </label>
            <Input
              type='password'
              value={fintabloToken}
              onChange={e => setFintabloToken(e.target.value)}
              placeholder='Токен из настроек Финтабло'
              required
              autoComplete='off'
            />
          </div>
        )}

        {mode === 'register' && (
          <ConsentCheckbox checked={consentAccepted} onChange={setConsentAccepted} />
        )}

        {error && <div style={s.error}>{error}</div>}

        <Button
          type='submit'
          variant='solid'
          disabled={mode === 'register' && !consentAccepted}
          loading={loading}
          style={{ width: '100%', marginTop: 4 }}
        >
          {mode === 'login' ? 'Войти' : 'Создать аккаунт'}
        </Button>

        {mode === 'login' && (
          <button style={s.link} type='button' onClick={startRecovery}>
            Забыли пароль?
          </button>
        )}
      </form>
    </AuthCard>
  )
}
